import { useCallback, useEffect, useRef, useState } from "react";
import InputError from "./InputError";
import getClassName from "../../utilities/getClassName";
import Label from "./Label";
import errorCross from "../../assets/icons/error-cross.svg";
import tickGreen from "../../assets/icons/tick-green.svg";
import Fieldset from "./Fieldset";
import CharacterCount from "./CharacterCount";
import "react-phone-number-input/style.css";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import useGeoLocation from "react-ipgeolocation";

// Map a field id/validation to a specific HTML autocomplete token so browsers
// and assistive tech can identify the input's purpose (WCAG 2.2 SC 1.3.5).
const getAutoComplete = ({ id, validation, type, matchPassword }) => {
    const key = (id || "").toLowerCase();

    if (validation === "email" || key.includes("email")) return "email";
    if (type === "password" || key.includes("password")) {
        // A confirm-password field passes matchPassword; signup fields are "new".
        return matchPassword ? "new-password" : "current-password";
    }
    if (key.includes("phone") || key.includes("tel") || key.includes("mobile")) return "tel";
    if (key.includes("firstname") || key.includes("forename") || key === "name" || key === "givenname")
        return "given-name";
    if (key.includes("surname") || key.includes("lastname") || key.includes("familyname")) return "family-name";
    if (key.includes("company") || key.includes("organisation") || key.includes("organization")) return "organization";
    if (key.includes("postcode") || key.includes("postalcode") || key.includes("zip")) return "postal-code";
    if (key.includes("address") || key.includes("street")) return "street-address";
    if (key.includes("city") || key.includes("town")) return "address-level2";
    if (key.includes("country")) return "country-name";

    return "on";
};

const TextInput = ({
    value,
    label,
    id,
    type,
    required,
    validation,
    defaultValue,
    maxLength,
    showCharacterCount = false,
    disabled,
    validateForm,
    matchPassword,
    classNames,
}) => {
    const [characterCount, setCharacterCount] = useState(String(defaultValue ?? "").length);

    // State
    const [error, setError] = useState("");
    const [showError, setShowError] = useState(false);
    const [focussed, setFocussed] = useState(id === "phone");
    const [interacted, setInteracted] = useState(false);
    const [skipAnimation, setSkipAnimation] = useState(false);

    // Hooks
    const input = useRef();
    const location = useGeoLocation();

    const getCountryCode = useCallback(() => {
        if (id === "phone") {
            const select = input.current.parentNode.querySelector(".PhoneInputCountrySelect");

            return select?.value;
        }
    }, [id]);

    // Validate input
    const validationHandler = useCallback(
        (blurOccurred) => {
            if (showCharacterCount) setCharacterCount(input.current.value.length);
            if (blurOccurred === true) {
                setShowError(true);
            }

            if (value) {
                value(null);

                const currentValue = input.current.value.replace(/\s/g, "");

                switch (true) {
                    case required && currentValue.length === 0:
                        setError(`${label.replace("*", "")} is required`);
                        return;
                    case matchPassword && (showError || blurOccurred === true) && matchPassword !== currentValue:
                        setError(`Passwords do no match`);
                        return;
                    case required &&
                        (showError || blurOccurred === true) &&
                        validation === "email" &&
                        !/\S+@\S+\.\S+/.test(currentValue):
                        setError(`Invalid email`);
                        return;
                    case id === "phone" && getCountryCode() === "ZZ":
                        setError(`Please choose a country`);
                        return;
                    case id === "phone" && !isValidPhoneNumber(currentValue):
                        setError(`Invalid phone number`);
                        return;
                    default:
                        setInteracted(true);
                        setError("");
                        value(input.current.value);
                        return;
                }
            }
        },
        [label, matchPassword, required, showError, validation, value, getCountryCode, id, showCharacterCount]
    );

    // On blur handler
    const onBlurHandler = () => {
        validationHandler(true);
        setSkipAnimation(false);
        const currentValue = input.current.value.replace(/\s/g, "");
        if (currentValue.length === 0) {
            setFocussed(false);
        }
    };

    // "Latest ref" so this effect doesn't depend on the validationHandler /
    // value identity — many callers pass an inline arrow for `value`, which
    // would re-fire this effect every render and call validationHandler →
    // value() → parent setState → loop (same trap as LuggageDropdown).
    const validationHandlerRef = useRef(validationHandler);
    validationHandlerRef.current = validationHandler;

    // Force validation / Check for autofill / Default updated
    useEffect(() => {
        if (validateForm) {
            validationHandlerRef.current();
        }
        if (input.current.value) {
            setFocussed(true);
            setSkipAnimation(true);
        }
        if (defaultValue) {
            input.current.value = defaultValue;
        }
        if (showCharacterCount) setCharacterCount(input.current.value.length);
    }, [defaultValue, validateForm, showCharacterCount]);

    useEffect(() => {
        if (id === "phone") {
            const select = input.current.parentNode.querySelector("select");
            if (select) {
                const internationalOption = select.querySelector('option[value="ZZ"]');
                let separator = select.querySelector("option[disabled]");
                if (!separator) {
                    separator = document.createElement("option");
                    separator.innerHTML = "---------------";
                    separator.disabled = true;
                }
                const priority = [
                    select.querySelector('option[value="GB"]'),
                    select.querySelector('option[value="US"]'),
                    select.querySelector('option[value="FR"]'),
                    select.querySelector('option[value="DE"]'),
                    select.querySelector('option[value="IT"]'),
                    select.querySelector('option[value="CH"]'),
                    separator,
                ];

                priority.forEach((option) => {
                    if (option) {
                        select.insertBefore(option, internationalOption);
                    }
                });
            }
        }
        // input.current is intentionally not listed: mutating a ref does not
        // re-render, so depending on it is meaningless. The effect runs once
        // per `id` (which only ever differs between TextInput instances).
    }, [id]);

    return (
        <Fieldset marginBottom>
            <div
                className={getClassName([
                    "text-input text-input--margin-bottom",
                    disabled && "text-input--disabled",
                    ...(classNames || []),
                ])}
            >
                {error && <InputError id={`${id}-error`}>{error}</InputError>}
                {id === "phone" ? (
                    <PhoneInput
                        ref={input}
                        international
                        autoComplete="tel"
                        value={defaultValue}
                        defaultCountry={location?.country}
                        onFocus={() => setFocussed(true)}
                        onBlur={onBlurHandler}
                        onChange={validationHandler}
                        aria-invalid={error ? "true" : undefined}
                        aria-describedby={
                            [error && `${id}-error`, showCharacterCount && `${id}-count`].filter(Boolean).join(" ") ||
                            undefined
                        }
                    />
                ) : (
                    <input
                        defaultValue={defaultValue}
                        ref={input}
                        type={type === "password" ? "password" : "text"}
                        id={id}
                        name={id}
                        autoComplete={getAutoComplete({ id, validation, type, matchPassword })}
                        onFocus={() => setFocussed(true)}
                        maxLength={maxLength}
                        onBlur={onBlurHandler}
                        onChange={validationHandler}
                        aria-invalid={error ? "true" : undefined}
                        aria-describedby={
                            [error && `${id}-error`, showCharacterCount && `${id}-count`].filter(Boolean).join(" ") ||
                            undefined
                        }
                        className={getClassName([
                            "text-input__input",
                            error && "text-input__input--error",
                            interacted && !error && "text-input__input--success",
                        ])}
                        disabled={disabled}
                    />
                )}

                {error && <img className="text-input__error-cross" src={errorCross} alt="" />}
                {interacted && !error && <img className="text-input__tick" src={tickGreen} alt="" />}
                {label && (
                    <Label minimised={focussed} skipAnimation={skipAnimation} htmlFor={id}>
                        {label}
                    </Label>
                )}
            </div>
            {showCharacterCount && <CharacterCount id={`${id}-count`} count={characterCount} maxLength={maxLength} />}
        </Fieldset>
    );
};

export default TextInput;
